// Roadmap 3b (operator 2026-10-16) — "Check against the tax authority's
// records", on each country's return page: the operator uploads the file
// the authority holds (or the one filed with it), and the return is
// compared with it beside the boxes it checks. Advisory: never blocks.
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  authorityCheck, deleteAuthorityFile, listAuthorityFiles, uploadAuthorityFile,
} from "../../lib/api";
import { errorMessage } from "../../lib/errorMessage";

const fmtPeriod = (p) => !p ? "" : p.month
  ? `${p.year}-${String(p.month).padStart(2, "0")}` : p.quarter ? `${p.year} Q${p.quarter}` : String(p.year);

export const AuthorityCheckPanel = ({ calcId, country }) => {
  const { t } = useTranslation();
  const [info, setInfo] = useState(null);
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [confirmId, setConfirmId] = useState(null);   // A2-581 lead 28

  const load = useCallback(async () => {
    try {
      const i = await listAuthorityFiles(calcId, country);
      setInfo(i);
      setCheck(i.supported && i.files.length ? await authorityCheck(calcId, country) : null);
    } catch (e) { setErr(errorMessage(e)); }
  }, [calcId, country]);
  useEffect(() => { load(); }, [load]);

  if (!info || !info.supported) return null;

  const onFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true); setErr(null);
    try { await uploadAuthorityFile(calcId, country, f); await load(); }
    catch (x) { setErr(errorMessage(x)); }
    finally { setBusy(false); }
  };
  // A2-581 lead 28: Remove deleted at once with no confirm, and a failure
  // was an unhandled rejection the operator never saw.
  const remove = async (id) => {
    setBusy(true); setErr(null);
    try { await deleteAuthorityFile(calcId, country, id); setConfirmId(null); await load(); }
    catch (x) { setErr(errorMessage(x)); }
    finally { setBusy(false); }
  };

  return (
    <section data-testid="authority-check" className="mb-6 rounded-xl border border-slate-300 dark:border-slate-600 p-4 bg-white dark:bg-slate-900/60">
      <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-50">{t("ui.country_return.authority_check.title", { defaultValue: "Check against the tax authority's records" })}</h2>
      <p className="text-xs text-slate-600 dark:text-slate-300 mt-1">
        {t("ui.country_return.authority_check.intro", { what: info.what_to_upload, defaultValue: `Upload ${info.what_to_upload}. The return is compared with it; nothing on the return changes.` })}
      </p>
      <label className="inline-flex items-center mt-2 text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-400 dark:border-slate-500 cursor-pointer text-slate-800 dark:text-slate-100">
        {busy ? t("ui.country_return.authority_check.reading", { defaultValue: "Reading the file…" }) : t("ui.country_return.authority_check.upload", { defaultValue: "Upload file" })}
        <input data-testid="authority-file-input" type="file" accept=".xml,.csv,.xlsx" className="sr-only" onChange={onFile} disabled={busy} />
      </label>
      {err && <div data-testid="authority-error" className="text-xs text-rose-700 dark:text-rose-300 mt-2">{err}</div>}
      {info.files.length > 0 && (
        <ul className="mt-3 text-xs text-slate-700 dark:text-slate-200 space-y-1">
          {info.files.map((f) => (
            <li key={f.id} className="flex gap-3 items-center">
              <span className="tabular-nums">{fmtPeriod(f.period)}</span>
              <span>{f.filename}</span>
              <span className="text-slate-500">{t("ui.country_return.authority_check.documents", { count: f.document_count, defaultValue: `${f.document_count} documents` })}</span>
              {confirmId === f.id ? (
                <span className="flex gap-2 items-center" data-testid={`authority-remove-confirm-${f.id}`}>
                  {t("ui.country_return.authority_check.remove_confirm", { defaultValue: "Remove this file?" })}
                  <button type="button" className="underline font-semibold" disabled={busy}
                    data-testid={`authority-remove-yes-${f.id}`} onClick={() => remove(f.id)}>
                    {t("ui.country_return.authority_check.remove_yes", { defaultValue: "Yes, remove" })}
                  </button>
                  <button type="button" className="underline" onClick={() => setConfirmId(null)}>
                    {t("ui.country_return.authority_check.remove_no", { defaultValue: "Keep" })}
                  </button>
                </span>
              ) : (
                <button type="button" className="underline" disabled={busy} data-testid={`authority-remove-${f.id}`}
                  onClick={() => setConfirmId(f.id)}>{t("ui.country_return.authority_check.remove", { defaultValue: "Remove" })}</button>
              )}
            </li>
          ))}
        </ul>
      )}
      {check && !check.checked && (
        <div className="text-xs text-amber-700 dark:text-amber-300 mt-3">{check.message}</div>
      )}
      {check && check.checked && (
        <div className="mt-3 overflow-x-auto">
          <div data-testid="authority-verdict" className={`text-xs font-semibold mb-2 ${check.all_same ? "text-emerald-700 dark:text-emerald-300" : "text-amber-700 dark:text-amber-300"}`}>
            {check.all_same
              ? t("ui.country_return.authority_check.verdict_same", { defaultValue: "The return matches the filed control statement." })
              : t("ui.country_return.authority_check.verdict_differs", { defaultValue: "The return differs from the filed control statement — see below." })}
          </div>
          <table className="text-xs w-full tabular-nums">
            <thead><tr className="text-left text-slate-500">
              <th className="pr-3 font-normal">{t("ui.country_return.authority_check.col_check", { defaultValue: "Check" })}</th>
              <th className="pr-3 font-normal text-right">{t("ui.country_return.authority_check.col_control", { defaultValue: "Control statement" })}</th>
              <th className="pr-3 font-normal text-right">{t("ui.country_return.authority_check.col_return", { defaultValue: "Return" })}</th>
              <th className="font-normal text-right">{t("ui.country_return.authority_check.col_difference", { defaultValue: "Difference" })}</th>
            </tr></thead>
            <tbody>
              {check.rules.map((r) => (
                <tr key={r.rule} className={r.same ? "" : "text-amber-800 dark:text-amber-200 font-semibold"}>
                  <td className="pr-3">{r.rule}</td><td className="pr-3 text-right">{r.control_statement}</td>
                  <td className="pr-3 text-right">{r.return}</td><td className="text-right">{r.same ? "—" : r.difference}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {["sales_only_in_control_statement", "purchases_only_in_control_statement"].map((k) =>
            check.documents[k].length > 0 && (
              <div key={k} className="text-xs mt-2 text-slate-700 dark:text-slate-200">
                {k.startsWith("sales")
                  ? t("ui.country_return.authority_check.sales_only", { defaultValue: "Sales documents in the control statement but not in your upload: " })
                  : t("ui.country_return.authority_check.purchases_only", { defaultValue: "Purchase documents in the control statement but not in your upload: " })}
                <span className="tabular-nums">{check.documents[k].join(", ")}</span>
              </div>
            ))}
        </div>
      )}
    </section>
  );
};
